import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Sparkles,
  ArrowLeft,
  FileText,
  Upload,
  Plus,
  X,
  Layers,
  History,
  ChevronRight,
} from "lucide-react";
import QuestionBankClient from "../data/api/QuestionBankClient";
import { ModuleItem } from "../data/interfaces/QuestionBankInterfaces";
import CustomSelect, { SelectOption } from "../components/CustomSelect";
import Loader from "../../../../app/shared/components/Loader";
import Alert from "../../../../app/shared/components/Alert";
import "../styles/QuestionBankStyles.css";

const GenerateAIQuestionPage: React.FC = () => {
  const navigate = useNavigate();
  const [modules, setModules] = useState<ModuleItem[]>([]);
  const [selectedModule, setSelectedModule] = useState<string>("");
  const [moduleName, setModuleName] = useState<string>("");

  // Add Module Modal state
  const [showAddModuleModal, setShowAddModuleModal] = useState<boolean>(false);
  const [newModuleName, setNewModuleName] = useState<string>("");
  const [savingModule, setSavingModule] = useState<boolean>(false);

  // Form State
  const [contextType, setContextType] = useState<"text" | "file">("text");
  const [promptText, setPromptText] = useState<string>("");
  const [noOfQuestions, setNoOfQuestions] = useState<number>(5);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [alertConfig, setAlertConfig] = useState<{
    show: boolean;
    title: string;
    message: string;
    type: "success" | "warning" | "error";
  }>({
    show: false,
    title: "",
    message: "",
    type: "success",
  });

  const fetchModules = async () => {
    try {
      const res = await QuestionBankClient.listModules();
      if (res && res.modules && res.modules.length > 0) {
        const modItems = res.modules.map((m: any) => ({
          id: m.module_id,
          name: m.module_name,
          industry_id: "ind_standard",
        }));
        setModules(modItems);
        if (!selectedModule) {
          setSelectedModule(modItems[0].id);
          setModuleName(modItems[0].name);
        }
      } else {
        setModules([]);
      }
    } catch (err) {
      console.error("Error fetching modules:", err);
    }
  };

  useEffect(() => {
    fetchModules();
  }, []);

  const handleModuleChange = (val: string) => {
    setSelectedModule(val);
    const m = modules.find((mod) => mod.id === val);
    if (m) setModuleName(m.name);
  };

  const handleCreateModule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newModuleName.trim()) return;
    setSavingModule(true);
    try {
      const res = await QuestionBankClient.createModule(newModuleName.trim());
      await fetchModules();
      if (res && res.module) {
        setSelectedModule(res.module.module_id);
        setModuleName(res.module.module_name);
      }
      setShowAddModuleModal(false);
      setNewModuleName("");
      setAlertConfig({
        show: true,
        title: "Success",
        message: `New Module '${newModuleName.trim()}' created successfully!`,
        type: "success",
      });
    } catch (err: any) {
      setAlertConfig({
        show: true,
        title: "Error",
        message: err?.response?.data?.message || "Failed to create module.",
        type: "error",
      });
    } finally {
      setSavingModule(false);
    }
  };

  const [isDragActive, setIsDragActive] = useState<boolean>(false);

  const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB limit
  const ALLOWED_EXTENSIONS = [".pdf", ".doc", ".docx"];

  const validateFile = (file: File): boolean => {
    const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setAlertConfig({
        show: true,
        title: "Invalid File Format",
        message: `Supported file formats: ${ALLOWED_EXTENSIONS.join(", ")}`,
        type: "warning",
      });
      return false;
    }
    if (file.size > MAX_FILE_SIZE) {
      setAlertConfig({
        show: true,
        title: "File Too Large",
        message: `File size exceeds 25MB limit (${(file.size / 1024 / 1024).toFixed(2)} MB). Please upload a file smaller than 25MB.`,
        type: "warning",
      });
      return false;
    }
    return true;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
      } else {
        e.target.value = "";
      }
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedModule) {
      setAlertConfig({
        show: true,
        title: "Validation Error",
        message: "Please select or create a Target Module.",
        type: "warning",
      });
      return;
    }
    if (contextType === "text" && !promptText.trim()) {
      setAlertConfig({
        show: true,
        title: "Validation Error",
        message: "Please provide topic description or text context.",
        type: "warning",
      });
      return;
    }
    if (contextType === "file" && !selectedFile) {
      setAlertConfig({
        show: true,
        title: "Validation Error",
        message: "Please upload a PDF or text document context.",
        type: "warning",
      });
      return;
    }
    if (!noOfQuestions || noOfQuestions < 1 || noOfQuestions > 50) {
      setAlertConfig({
        show: true,
        title: "Validation Error",
        message: "Please specify a question count between 1 and 50.",
        type: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      let contextFileUrl = "";
      let contextFileName = "";

      if (contextType === "file" && selectedFile) {
        contextFileName = selectedFile.name;
        // Step 1: Call /create_upload_url to get uploadURL and downloadURL
        const uploadRes = await QuestionBankClient.createUploadUrl(
          selectedFile.name,
        );

        // Step 2: Upload file blob directly to S3 uploadURL
        if (uploadRes && uploadRes.uploadURL) {
          await QuestionBankClient.uploadFileToS3(
            uploadRes.uploadURL,
            selectedFile,
          );
          contextFileUrl = uploadRes.downloadURL || uploadRes.uploadURL;
        } else {
          throw new Error("Failed to obtain upload URL from server.");
        }
      }

      const res = await QuestionBankClient.generateAIQuestions({
        module_id: selectedModule,
        module_name: moduleName,
        context_type: contextType,
        user_prompt: promptText.trim(),
        context_text: promptText.trim(),
        context_file_name: contextFileName,
        context_file: contextFileUrl,
        no_of_questions_requested: noOfQuestions,
      });

      setLoading(false);
      setAlertConfig({
        show: true,
        title: "AI Question Generation",
        message: res?.detail || "Question Generation in Progress",
        type: "success",
      });

      setTimeout(() => {
        navigate("/dashboard/question-bank/ai-requests");
      }, 1000);
    } catch (err: any) {
      setLoading(false);
      setAlertConfig({
        show: true,
        title: "Generation Failed",
        message:
          err?.response?.data?.error ||
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to generate questions.",
        type: "error",
      });
    }
  };

  const moduleSelectOptions: SelectOption[] = modules.map((m) => ({
    value: m.id,
    label: m.name,
  }));

  const isAIGenerateValid = Boolean(
    selectedModule &&
    (contextType === "text" ? promptText.trim() : Boolean(selectedFile)) &&
    noOfQuestions > 0 &&
    noOfQuestions <= 50,
  );

  return (
    <div className="qb-container">
      <Loader
        isLoading={loading || savingModule}
        message={savingModule ? "Saving Module" : "Generating MCQs"}
      />

      <Alert
        show={alertConfig.show}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
        onClose={() => setAlertConfig((prev) => ({ ...prev, show: false }))}
      />

      {/* Header matching AddQuestionPage layout */}
      <div className="qb-header">
        <div>
          <h1 className="qb-title">
            <Sparkles size={26} color="#0d6e5b" /> Generate Questions
          </h1>
          {/* <p className="qb-subtitle">
            Auto-create high-quality MCQs from raw topic context or textbook PDF documents using Google Gemini 2.5 AI.
          </p> */}
        </div>

        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <button
            type="button"
            className="qb-btn qb-btn-outline"
            onClick={() => navigate("/dashboard/question-bank/ai-requests")}
          >
            <History size={16} /> View AI Question History{" "}
            <ChevronRight size={14} />
          </button>
          <button
            type="button"
            className="qb-btn qb-btn-outline"
            onClick={() => navigate("/dashboard/question-bank")}
          >
            <ArrowLeft size={16} /> Back to Question Bank
          </button>
        </div>
      </div>

      {/* Form Container filling container width matching AddQuestionPage */}
      <form className="qb-content-scroll" onSubmit={handleGenerate}>
        {/* Section 1: Target Module & Difficulty */}
        <div className="qb-form-section">
          <div className="qb-section-header">
            <Sparkles size={20} color="#0d6e5b" />
            <span>1. Target Module</span>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "20px",
            }}
          >
            <div className="qb-form-group" style={{ margin: 0 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <label className="qb-label" style={{ margin: 0 }}>
                  Target Module <span style={{ color: "#ef4444" }}>*</span>:
                </label>
                {modules.length > 0 && (
                  <button
                    type="button"
                    className="qb-btn qb-btn-outline"
                    style={{
                      padding: "4px 10px",
                      fontSize: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                    onClick={() => setShowAddModuleModal(true)}
                  >
                    <Plus size={14} /> Add New Module
                  </button>
                )}
              </div>
              {modules.length > 0 ? (
                <CustomSelect
                  options={moduleSelectOptions}
                  value={selectedModule}
                  onChange={handleModuleChange}
                  placeholder="Select Module..."
                />
              ) : (
                <div
                  style={{
                    padding: "12px 16px",
                    backgroundColor: "#FEF3C7",
                    borderRadius: "8px",
                    color: "#92400E",
                    fontSize: "13px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>No modules found. Please create a module.</span>
                  <button
                    type="button"
                    className="qb-btn qb-btn-primary"
                    style={{ padding: "4px 12px", fontSize: "12px" }}
                    onClick={() => setShowAddModuleModal(true)}
                  >
                    Create Module
                  </button>
                </div>
              )}
            </div>

            <div>
              <label
                className="qb-label"
                style={{ marginBottom: "8px", display: "block" }}
              >
                Number of Questions (1 - 50){" "}
                <span style={{ color: "#ef4444" }}>*</span>:
              </label>
              <input
                type="number"
                min={1}
                max={50}
                className="qb-input"
                value={noOfQuestions}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (isNaN(val)) {
                    setNoOfQuestions("" as any);
                  } else {
                    setNoOfQuestions(Math.min(50, Math.max(1, val)));
                  }
                }}
                placeholder="e.g. 5"
                style={{
                  height: "42px",
                  fontSize: "14px",
                  fontWeight: 600,
                }}
              />
            </div>
          </div>
        </div>

        {/* Section 2: Context Source & Prompt Details */}
        <div className="qb-form-section">
          <div className="qb-section-header">
            <FileText size={20} color="#0d6e5b" />
            <span>2. Context Source</span>
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label
              className="qb-label"
              style={{ display: "block", marginBottom: "8px" }}
            >
              Select Source <span style={{ color: "#ef4444" }}>*</span>:
            </label>
            <div style={{ display: "flex", gap: "16px" }}>
              <button
                type="button"
                className={`qb-btn ${contextType === "text" ? "qb-btn-primary" : "qb-btn-outline"}`}
                onClick={() => setContextType("text")}
                style={{
                  flex: 1,
                  padding: "12px 18px",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  fontWeight: 600,
                }}
              >
                <FileText size={18} />
                <span>Topic Description / Text</span>
              </button>
              <button
                type="button"
                className={`qb-btn ${contextType === "file" ? "qb-btn-primary" : "qb-btn-outline"}`}
                onClick={() => setContextType("file")}
                style={{
                  flex: 1,
                  padding: "12px 18px",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  fontWeight: 600,
                }}
              >
                <Upload size={18} />
                <span>Document Upload (PDF / DOC)</span>
              </button>
            </div>
          </div>

          {/* Context Details */}
          {contextType === "text" ? (
            <div className="qb-form-group" style={{ marginBottom: "8px" }}>
              <label
                className="qb-label"
                style={{ display: "block", marginBottom: "6px" }}
              >
                Topic Context
                <span style={{ color: "#ef4444" }}>*</span>:
              </label>
              <textarea
                className="qb-textarea"
                rows={6}
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
                placeholder="e.g. Java Virtual Machine (JVM) memory architecture, Garbage Collection algorithms (G1, ZGC), Heap vs Stack memory allocation, and classloader mechanics..."
              />
            </div>
          ) : (
            <div className="qb-form-group">
              <label
                className="qb-label"
                style={{ display: "block", marginBottom: "6px" }}
              >
                Upload File <span style={{ color: "#ef4444" }}>*</span>:
              </label>
              <div
                style={{
                  border: `2px dashed ${isDragActive ? "#0d6e5b" : "#438e76"}`,
                  borderRadius: "12px",
                  padding: "28px 24px",
                  textAlign: "center",
                  backgroundColor: isDragActive ? "#e6f4f1" : "#ddf2eb",
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                }}
                onClick={() =>
                  document.getElementById("ai-page-file-input")?.click()
                }
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "14px",
                  }}
                >
                  <Upload size={38} style={{ color: "#0d6e5b" }} />
                  <div style={{ textAlign: "left" }}>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "15px",
                        fontWeight: 600,
                        color: "#1e293b",
                      }}
                    >
                      {selectedFile
                        ? selectedFile.name
                        : "Select a PDF/DOC file to upload questions"}
                    </p>
                    <p
                      style={{
                        margin: "2px 0 0",
                        fontSize: "13px",
                        color: "#64748b",
                      }}
                    >
                      {selectedFile
                        ? `${(selectedFile.size / 1024 / 1024).toFixed(2)} MB`
                        : "or drag and drop here"}
                    </p>
                  </div>
                </div>
                <input
                  id="ai-page-file-input"
                  type="file"
                  accept=".pdf,.doc,.docx"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </div>

              <ul
                style={{
                  margin: "12px 0 0 0",
                  paddingLeft: "20px",
                  fontSize: "13px",
                  color: "#475569",
                  lineHeight: "1.6",
                }}
              >
                <li>Supported format: .pdf, .doc, .docx</li>
                <li>File Size: Less than 25MB</li>
              </ul>
            </div>
          )}

          {/* Action Footer matching AddQuestionPage */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              marginTop: "12px",
              paddingTop: "12px",
              borderTop: "1px solid #f1f5f9",
            }}
          >
            <button
              type="button"
              className="qb-btn qb-btn-outline"
              onClick={() => navigate("/dashboard/question-bank")}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="qb-btn qb-btn-primary"
              style={{ padding: "12px 28px" }}
              disabled={!isAIGenerateValid || loading}
            >
              <Sparkles size={18} />
              <span>Generate Questions</span>
            </button>
          </div>
        </div>
      </form>

      {/* Add New Module Modal */}
      {showAddModuleModal &&
        ReactDOM.createPortal(
          <div
            className="qb-modal-overlay"
            onClick={() => setShowAddModuleModal(false)}
          >
            <div
              className="qb-modal-content"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "480px" }}
            >
              <div className="qb-modal-header">
                <h3
                  style={{
                    margin: 0,
                    fontSize: "18px",
                    fontWeight: "700",
                    color: "#07473b",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <Layers size={18} color="#0d6e5b" /> Create New Module
                </h3>
                <button
                  className="qb-modal-close"
                  onClick={() => setShowAddModuleModal(false)}
                >
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleCreateModule}>
                <div className="qb-form-group">
                  <label className="qb-label">
                    Module Name <span style={{ color: "#ef4444" }}>*</span>:
                  </label>
                  <input
                    type="text"
                    className="qb-input"
                    placeholder="e.g. System Design, Cloud Computing, Algorithms"
                    value={newModuleName}
                    onChange={(e) => setNewModuleName(e.target.value)}
                    autoFocus
                    required
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "12px",
                    marginTop: "24px",
                  }}
                >
                  <button
                    type="button"
                    className="qb-btn qb-btn-outline"
                    onClick={() => setShowAddModuleModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="qb-btn qb-btn-primary"
                    disabled={savingModule || !newModuleName.trim()}
                  >
                    {savingModule ? "Saving..." : "Create module"}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default GenerateAIQuestionPage;
